import * as THREE from 'three';
import { LODStaticObject, StaticObjectType } from './LODStaticObject';
import { BoundingSphere } from '../../physics/BoundingVolume';

/**
 * Optimized Rock with Level of Detail support
 * Uses the improved LODStaticObject base class for efficient LOD transitions
 * Composed of several low-poly shapes with flat shading to show triangles
 */
export class LODRock extends LODStaticObject {
  // Track scale value to properly size the bounding volume
  private rockScale: THREE.Vector3;
  
  constructor() {
    // Create materials with flat shading to show the triangles
    const rockMaterial = new THREE.MeshStandardMaterial({
      color: 0x5D5D5D, // Dark gray
      roughness: 0.9,
      metalness: 0.2,
      flatShading: true, // Enable flat shading to see triangles
      shadowSide: THREE.FrontSide // Properly cast shadows
    });
    
    const darkerRockMaterial = new THREE.MeshStandardMaterial({
      color: 0x4A4A4A, // Darker gray for medium LOD
      roughness: 0.9,
      metalness: 0.2,
      flatShading: true,
      shadowSide: THREE.FrontSide
    });
    
    const lighterRockMaterial = new THREE.MeshStandardMaterial({
      color: 0x6A6A6A, // Lighter gray for low LOD
      roughness: 0.9,
      metalness: 0.2,
      flatShading: true,
      shadowSide: THREE.FrontSide
    });
    
    // Randomize the scale a bit to create variety in rock sizes
    // Between 0.6 and 1.2 in all dimensions
    const scaleX = 0.6 + Math.random() * 0.6;
    const scaleY = 0.6 + Math.random() * 0.6;
    const scaleZ = 0.6 + Math.random() * 0.6;
    
    // Create a temporary vector for the scale to use in super() call
    const rockScale = new THREE.Vector3(scaleX, scaleY, scaleZ);
    
    // Create with rock-specific configuration
    super({
      type: StaticObjectType.ROCK,
      name: 'LODRock',
      baseScale: rockScale,
      materialOverrides: {
        // Use different rock materials for each LOD level
        high: rockMaterial,
        medium: darkerRockMaterial,
        low: lighterRockMaterial
      }
    });
    
    // Now we can store the scale after super() has been called
    this.rockScale = rockScale;
    
    // Add a bounding sphere for collision
    this.createBoundingVolume();
  }
  
  /**
   * Create bounding volume for collision detection based on rock size
   */
  private createBoundingVolume(): void {
    // Calculate the bounding sphere radius based on maximum dimension
    // Add a slight padding for better collision detection
    const maxDimension = Math.max(
      this.rockScale.x,
      this.rockScale.y,
      this.rockScale.z
    );
    
    // The radius needs to be large enough to encompass all rock parts
    // The base geometry has parts extending about 0.5 units in each direction
    const radius = maxDimension * 0.6; // Slightly larger than half to cover all parts
    
    const boundingSphere = new BoundingSphere(radius, this.getPosition());
    this.setBoundingVolume(boundingSphere);
  }
} 